// src/features/auth/components/LoginForm.tsx
import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { TextField, Button, Typography, Box, CircularProgress, Paper, Alert } from '@mui/material';
import { loginUser } from '../api/authApi';
import useAuthStore from '../../../store/authStore';
import clarityService from '../../../services/clarityService';
import { SocialLoginButtons } from '../../../components/auth';
import { OAuthError } from '../../../types/oauth';
import { User } from '../../../services/authService';
import { brand } from '../../../config/brand';

const LoginForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, error } = useAuthStore();

  // Redirect back to the page the user was trying to access, or default to dashboard
  const redirectTo = (location.state as any)?.from?.pathname || '/dashboard';

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formErrors, setFormErrors] = useState({
    email: '',
    password: '',
  });
  const [oauthError, setOauthError] = useState<string | null>(null);

  // Handle OAuth login success
  const handleOAuthSuccess = (user: User, isNewUser?: boolean) => {
    // Track successful OAuth login
    if (isNewUser) {
      clarityService.event('OAuth User Registration Success');
      clarityService.upgrade('New User OAuth Registration');
    } else {
      clarityService.event('OAuth User Login Success');
    }

    navigate('/dashboard');
  };

  // Handle OAuth login error
  const handleOAuthError = (error: OAuthError) => {
    clarityService.event('OAuth User Login Failed');
    setOauthError(error.message);
  };

  // Validate form
  const validateForm = (): boolean => {
    let valid = true;
    const newErrors = { email: '', password: '' };
    
    // Email validation
    if (!email) {
      newErrors.email = 'Email is required';
      valid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
      valid = false;
    }
    
    // Password validation
    if (!password) {
      newErrors.password = 'Password is required';
      valid = false;
    }
    
    setFormErrors(newErrors);
    return valid;
  };

  // Handle login submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      await loginUser({ email, password });

      // Track successful login event in Clarity
      clarityService.event('User Login Success');

      navigate(redirectTo);
    } catch (error) {
      // Track failed login attempt in Clarity
      clarityService.event('User Login Failed');

      // Error is handled in the API layer and stored in the auth store
      console.error("Login failed:", error instanceof Error ? error.message : String(error));
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 400, width: '100%' }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        {`Login to ${brand.name}`}
      </Typography>
      
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          margin="normal"
          required
          fullWidth
          id="email"
          label="Email Address"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!formErrors.email}
          helperText={formErrors.email}
          disabled={isLoading}
        />
        
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type="password"
          id="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={!!formErrors.password}
          helperText={formErrors.password}
          disabled={isLoading}
        />
        
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          sx={{ mt: 3, mb: 2 }}
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
        </Button>
        
        <Box display="flex" justifyContent="space-between" mt={2}>
          <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
            <Typography variant="body2" color="primary">
              Forgot password?
            </Typography>
          </Link>

          <Link to="/register" state={location.state} style={{ textDecoration: 'none' }}>
            <Typography variant="body2" color="primary">
              Don't have an account? Sign Up
            </Typography>
          </Link>
        </Box>

        {/* Social Login Options */}
        <SocialLoginButtons
          onSuccess={handleOAuthSuccess}
          onError={handleOAuthError}
          disabled={isLoading}
          context="signin"
          error={oauthError}
        />
      </Box>
    </Paper>
  );
};

export default LoginForm;